import React, { ReactNode } from "react";
import { X } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { Box, Flex, IconButton, Theme, Text } from "@radix-ui/themes";

interface ModalProps {
  isOpen: boolean;
  children: ReactNode;
  title?: string;
  description?: string;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  className?: string;
  actions?: ReactNode;
  onClose: () => void;
  /**
   * When false, Radix Dialog skips its `pointer-events: none` body lock and
   * focus trap. Set for dialogs that host other Radix overlays (Select,
   * Popover, SearchableSelect) whose portaled content would otherwise be
   * click-blocked.
   */
  modal?: boolean;
}

const sizeClasses = {
  sm: "400px",
  md: "600px",
  lg: "800px",
  xl: "1000px",
  full: "95vw",
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  children,
  title,
  description,
  size = "md",
  className,
  actions,
  onClose,
  modal = true,
}) => {
  return (
    <Dialog.Root open={isOpen} modal={modal} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Theme>
          <Dialog.Overlay
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              backdropFilter: "blur(2px)",
              zIndex: 11000,
            }}
          />
          <Dialog.Content
            className={className}
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              maxWidth: sizeClasses[size],
              maxHeight: "calc(100vh - 2rem)",
              display: "flex",
              flexDirection: "column",
              padding: "10px",
              backgroundColor: "var(--color-panel-solid)",
              borderRadius: "var(--radius-4)",
              boxShadow: "var(--shadow-5)",
              zIndex: 11000,
              width: sizeClasses[size],
            }}
            onEscapeKeyDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onPointerDownOutside={!modal ? (e) => e.preventDefault() : undefined}
            onInteractOutside={!modal ? (e) => e.preventDefault() : undefined}
          >
            {(title || description) && (
              <Flex
                justify="between"
                align="center"
                px="4"
                py="3"
                style={{
                  borderBottom: "1px solid var(--gray-a5)",
                  flexShrink: 0,
                }}
              >
                <Flex direction="column" gap="1">
                  {title && (
                    <Dialog.Title asChild>
                      <Text
                        size="5"
                        weight="medium"
                        style={{
                          marginBottom: description ? "var(--space-1)" : 0,
                        }}
                      >
                        {title}
                      </Text>
                    </Dialog.Title>
                  )}
                  {description && (
                    <Dialog.Description asChild>
                      <Text size="2" color="gray">
                        {description}
                      </Text>
                    </Dialog.Description>
                  )}
                </Flex>
                <Dialog.Close>
                  <IconButton
                    variant="ghost"
                    color="gray"
                    size="2"
                    aria-label="Close dialog"
                  >
                    <X className="h-4 w-4" />
                  </IconButton>
                </Dialog.Close>
              </Flex>
            )}

            <Box
              style={{
                flex: 1,
                overflow: "auto",
                minHeight: 0,
              }}
            >
              {children}
            </Box>

            {actions && (
              <Flex
                justify="end"
                gap="3"
                p="4"
                style={{
                  borderTop: "1px solid var(--gray-a5)",
                  flexShrink: 0,
                }}
              >
                {actions}
              </Flex>
            )}
          </Dialog.Content>
        </Theme>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Modal;
